import { AssetType } from '@amzn/fire-tv-campaign-automation-service';
import selectedShell from 'public/images/FullScreenFeatureRotator_Selected_Shell.png';
import unselectedShell from 'public/images/FullScreenFeatureRotator_Unselected_Shell.png';
import {
  PreviewStateConfig,
  LayerType,
  BackgroundTransformType,
  LogoTransformType,
  GradientDirection,
} from 'src/utils/preview/types';

import { FireTVPreviewState } from '../types';

/**
 * FireTV Feature Rotator State Configurations
 *
 * Layer-based rendering configs for each FireTV state
 * Consumed by FireTV renderer via unified config
 *
 * States:
 * - Selected: Upscaled background (2410x904) with -447 x offset, fixed logo, shell
 * - Unselected: Standard background with -33 y offset, fixed logo, shell
 * - Video: Video trailer with center crop, no logo/shell
 */
export const FIRETV_STATE_CONFIGS: Partial<Record<FireTVPreviewState, PreviewStateConfig>> = {
  [FireTVPreviewState.SELECTED]: {
    label: 'Selected',
    altText: 'Please upload both background image and logo to preview Selected State',
    dimensions: { width: 1920, height: 1080 },
    layers: [
      {
        type: LayerType.BACKGROUND,
        transform: {
          type: BackgroundTransformType.RESIZE,
          x: -447,
          y: 0,
          width: 2410,
          height: 904,
        },
      },
      {
        type: LayerType.LOGO,
        transform: {
          type: LogoTransformType.FIXED,
          x: 104,
          y: 270,
          width: 640,
          height: 260,
        },
      },
      {
        type: LayerType.SHELL,
        src: selectedShell,
      },
    ],
    assetRequirements: [AssetType.BACKGROUND_IMAGE, AssetType.LOGO],
  },
  [FireTVPreviewState.UNSELECTED]: {
    label: 'Unselected',
    altText: 'Please upload both background image and logo to preview Unselected State',
    dimensions: { width: 1920, height: 1080 },
    layers: [
      {
        type: LayerType.BACKGROUND,
        transform: {
          type: BackgroundTransformType.RESIZE,
          x: 0,
          y: -33,
        },
      },
      {
        type: LayerType.LOGO,
        transform: {
          type: LogoTransformType.FIXED,
          x: 104,
          y: 56,
          width: 640,
          height: 260,
        },
      },
      {
        type: LayerType.SHELL,
        src: unselectedShell,
      },
    ],
    assetRequirements: [AssetType.BACKGROUND_IMAGE, AssetType.LOGO],
  },
  [FireTVPreviewState.VIDEO]: {
    label: 'Video',
    altText: 'Please upload a video file to preview video trailer',
    dimensions: { width: 1920, height: 1080 },
    layers: [
      {
        type: LayerType.VIDEO,
        transform: {
          type: BackgroundTransformType.CENTER_CROP,
        },
      },
    ],
    assetRequirements: [AssetType.VIDEO_TRAILER],
  },
  [FireTVPreviewState.SCREEN_SAVER]: {
    label: 'Screen Saver',
    altText: 'Please upload background image to preview Screen Saver',
    dimensions: { width: 1920, height: 1080 },
    layers: [
      {
        type: LayerType.BACKGROUND,
        transform: {
          type: BackgroundTransformType.RESIZE,
          x: 0,
          y: 0,
        },
      },
      {
        type: LayerType.GRADIENT,
        direction: GradientDirection.HORIZONTAL,
        colorStops: [
          { position: 0, color: 'rgba(0, 0, 0, 0.7)' },
          { position: 1, color: 'rgba(0, 0, 0, 0)' },
        ],
        bounds: { x: 0, y: 0, width: 960, height: 1080 },
      },
      {
        type: LayerType.LOGO,
        transform: {
          type: LogoTransformType.FIXED,
          x: 60,
          y: 360,
          width: 640,
          height: 260,
        },
      },
    ],
    assetRequirements: [AssetType.BACKGROUND_IMAGE, AssetType.LOGO],
  },
};

export const FIRETV_FEATURE_ROTATOR_STATE_CONFIGS: Partial<
  Record<FireTVPreviewState, PreviewStateConfig>
> = {
  [FireTVPreviewState.SELECTED]: FIRETV_STATE_CONFIGS[FireTVPreviewState.SELECTED]!,
  [FireTVPreviewState.UNSELECTED]: FIRETV_STATE_CONFIGS[FireTVPreviewState.UNSELECTED]!,
  [FireTVPreviewState.VIDEO]: FIRETV_STATE_CONFIGS[FireTVPreviewState.VIDEO]!,
};

export const FIRETV_SCREEN_SAVER_STATE_CONFIGS: Partial<
  Record<FireTVPreviewState, PreviewStateConfig>
> = {
  [FireTVPreviewState.SCREEN_SAVER]: FIRETV_STATE_CONFIGS[FireTVPreviewState.SCREEN_SAVER]!,
};
